import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Pagination,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from 'api/client';

const borderColor = 'rgba(245, 158, 11, 0.35)';

const cardSx = {
  border: '1px solid',
  borderColor,
  borderRadius: 3,
  boxShadow: 'none'
};

const actionButtonSx = {
  textTransform: 'none',
  fontWeight: 700,
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: '#f59e0b',
  border: '1px solid #f59e0b',
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#f59e0b' }
};
const EMPTY_LOAD_PARAMS = Object.freeze({});

function parseError(error, fallback = 'Request failed.') {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  if (payload.detail) return payload.detail;
  const first = Object.keys(payload)[0];
  if (first) {
    const value = payload[first];
    if (Array.isArray(value)) return value[0];
    if (typeof value === 'string') return value;
  }
  return fallback;
}

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function parseFormPayload(form, fields) {
  const out = {};
  for (const field of fields) {
    const value = form[field.name];
    if (field.type === 'json') {
      if (value === '' || value == null) {
        out[field.name] = field.defaultJson ?? {};
      } else {
        try {
          out[field.name] = typeof value === 'string' ? JSON.parse(value) : value;
        } catch {
          throw new Error(`${field.label || field.name} must be valid JSON, like {"name": "value"}.`);
        }
      }
      continue;
    }
    if (field.type === 'number') {
      out[field.name] = value === '' || value == null ? null : Number(value);
      continue;
    }
    if (field.type === 'switch') {
      out[field.name] = Boolean(value);
      continue;
    }
    if (field.type === 'multiline') {
      out[field.name] = value ?? '';
      continue;
    }
    if (value === '' && field.nullable) {
      out[field.name] = null;
      continue;
    }
    out[field.name] = value;
  }
  return out;
}

function toFormValue(row, fields) {
  const out = {};
  fields.forEach((field) => {
    const value = row?.[field.name];
    if (field.type === 'json') {
      out[field.name] = value == null ? '{}' : JSON.stringify(value, null, 2);
      return;
    }
    if (field.type === 'switch') {
      out[field.name] = Boolean(value);
      return;
    }
    out[field.name] = value ?? field.defaultValue ?? '';
  });
  return out;
}

const AdminResourceCrudPage = ({
  title,
  subtitle,
  icon: IconComponent,
  endpoint,
  columns,
  fields = [],
  initialData = [],
  allowCreate = true,
  allowEdit = true,
  allowDelete = true,
  rowActions = [],
  loadParams,
  pageSize = 50,
  pageSizeOptions = [50, 100, 250]
}) => {
  const loadParamsKey = useMemo(() => JSON.stringify(loadParams || EMPTY_LOAD_PARAMS), [loadParams]);
  const effectiveLoadParams = useMemo(() => {
    try {
      return JSON.parse(loadParamsKey);
    } catch {
      return EMPTY_LOAD_PARAMS;
    }
  }, [loadParamsKey]);
  const [rows, setRows] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [effectivePageSize, setEffectivePageSize] = useState(pageSize);
  const [totalCount, setTotalCount] = useState(initialData.length);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const defaultForm = useMemo(() => toFormValue(null, fields), [fields]);
  const [form, setForm] = useState(defaultForm);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      Object.entries(effectiveLoadParams).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') params.set(key, String(val));
      });
      if (search.trim()) params.set('q', search.trim());
      params.set('page', String(page));
      params.set('page_size', String(effectivePageSize));
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const { data } = await api.get(`${endpoint}${suffix}`);
      const normalizedRows = normalizeList(data);
      setRows(normalizedRows);
      setTotalCount(typeof data?.count === 'number' ? data.count : normalizedRows.length);
    } catch (err) {
      setError(parseError(err, 'Failed to load records.'));
    } finally {
      setLoading(false);
    }
  }, [endpoint, effectiveLoadParams, search, page, effectivePageSize]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [loadParamsKey]);

  const openCreate = () => {
    setEditingRow(null);
    setForm(defaultForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setForm(toFormValue(row, fields));
    setDialogOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = parseFormPayload(form, fields);
      if (editingRow) {
        await api.patch(`${endpoint}${editingRow.id}/`, payload);
      } else {
        await api.post(endpoint, payload);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(parseError(err, 'Failed to save record.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingRow) return;
    setDeleting(true);
    setError('');
    try {
      await api.delete(`${endpoint}${deletingRow.id}/`);
      setDeleteOpen(false);
      setDeletingRow(null);
      await load();
    } catch (err) {
      setError(parseError(err, 'Failed to delete record.'));
    } finally {
      setDeleting(false);
    }
  };

  const activeCount = useMemo(() => rows.filter((row) => row.is_active === true).length, [rows]);

  const shown = rows.length;
  return (
    <Stack spacing={2.5} className="fuzzscan-page">
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={1.5}>
        <Box>
          <Stack direction="row" spacing={1.2} alignItems="center">
            {IconComponent ? <IconComponent sx={{ color: 'text.secondary' }} /> : null}
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{title}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            value={search}
            onChange={(event) => { setPage(1); setSearch(event.target.value); }}
            size="small"
            placeholder="Search"
            inputProps={{ 'aria-label': 'Search' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 220 }}
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading} aria-label="Refresh" sx={{ border: '1px solid', borderColor }}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          {allowCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={actionButtonSx}>
              Add
            </Button>
          )}
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {totalCount} record{totalCount === 1 ? '' : 's'}
        {rows.some((row) => typeof row.is_active === 'boolean') ? `, ${activeCount} active of the ${shown} shown` : ''}
      </Typography>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 0 }}>
          {loading && <LinearProgress />}
          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(245, 158, 11, 0.08)' }}>
                  {columns.map((col) => (
                    <TableCell key={col.key}>{col.label}</TableCell>
                  ))}
                  {(allowEdit || allowDelete || rowActions.length > 0) && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1}>
                      <Alert severity="info" sx={{ my: 1 }}>
                        No records found.
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      {columns.map((col) => (
                        <TableCell key={`${row.id}-${col.key}`}>
                          {col.render ? col.render(row) : row[col.key] ?? '-'}
                        </TableCell>
                      ))}
                      {(allowEdit || allowDelete || rowActions.length > 0) && (
                        <TableCell align="right">
                          {rowActions.map((actionItem) => {
                            if (typeof actionItem.visible === 'function' && !actionItem.visible(row)) {
                              return null;
                            }
                            const ActionIcon = actionItem.icon;
                            return (
                              <Tooltip title={actionItem.label} key={`${row.id}-${actionItem.label}`}>
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    try {
                                      await actionItem.onClick(row);
                                      await load();
                                    } catch (err) {
                                      setError(parseError(err, `Failed to run ${actionItem.label}.`));
                                    }
                                  }}
                                >
                                  {ActionIcon ? <ActionIcon fontSize="small" /> : null}
                                </IconButton>
                              </Tooltip>
                            );
                          })}
                          {allowEdit && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(row)}>
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {allowDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setDeletingRow(row);
                                  setDeleteOpen(true);
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ borderTop: '1px solid', borderColor, px: 2, py: 1.5 }}
          >
            <Typography variant="caption" color="text.secondary">
              Showing {rows.length ? (page - 1) * effectivePageSize + 1 : 0}-
              {Math.min(page * effectivePageSize, totalCount)} of {totalCount}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Pagination
                count={Math.max(1, Math.ceil(totalCount / effectivePageSize))}
                page={page}
                onChange={(_, value) => setPage(value)}
                size="small"
                color="primary"
                disabled={loading || totalCount <= effectivePageSize}
              />
              <TextField
                select
                size="small"
                label="Page size"
                value={effectivePageSize}
                onChange={(event) => {
                  setPage(1);
                  setEffectivePageSize(Number(event.target.value));
                }}
                sx={{ minWidth: 120 }}
              >
                {pageSizeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => (saving ? null : setDialogOpen(false))} maxWidth="md" fullWidth>
        <DialogTitle>{editingRow ? `Edit ${title}` : `Add ${title}`}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            {fields.map((field) => (
              <Grid size={{ xs: 12, sm: field.grid || 6 }} key={field.name}>
                {field.type === 'select' ? (
                  <TextField
                    select
                    label={field.label}
                    value={form[field.name] ?? ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
                    fullWidth
                    size="small"
                  >
                    {(field.options || []).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : field.type === 'switch' ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
                    <Typography variant="body2">{field.label}</Typography>
                    <Switch
                      checked={Boolean(form[field.name])}
                      onChange={(event) => setForm((prev) => ({ ...prev, [field.name]: event.target.checked }))}
                    />
                  </Stack>
                ) : (
                  <TextField
                    label={field.label}
                    value={form[field.name] ?? ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
                    fullWidth
                    size="small"
                    type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                    multiline={field.type === 'multiline' || field.type === 'json'}
                    minRows={field.type === 'multiline' || field.type === 'json' ? 3 : undefined}
                    InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving} sx={actionButtonSx}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => (deleting ? null : setDeleteOpen(false))} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Record</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Delete {deletingRow ? `"${String(deletingRow.name || deletingRow.title || deletingRow.key || deletingRow.label || `record ${deletingRow.id}`)}"` : 'this record'}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AdminResourceCrudPage;
